export interface CreativeBetSequencePreviewStep {
    stepLabel: string;
    timing?: string;
    subject?: string;
    copy: string;
}

export interface CreativeBetSequenceStep {
    stepLabel: string;
    direction: string;
    cta?: string;
    avoidRepeating?: string;
}

export interface CreativeBet {
    id: string;
    label: string;
    theme?: 'proof' | 'research' | 'pain' | 'angle' | 'channel' | 'custom';
    focus?: 'step1-refresh' | 'full-sequence-arc' | 'followup-recovery' | 'proof-rotation' | 'custom';
    preview: string;
    sequencePreview?: CreativeBetSequencePreviewStep[];
    sequenceStrategy?: CreativeBetSequenceStep[];
    rationale?: string;
    recommended?: boolean;
}

export interface CreativeBetAction {
    id: string;
    label: string;
    prompt: string;
}

export interface CreativeBetsData {
    campaignId: number;
    summary: string;
    bets: CreativeBet[];
    actions?: CreativeBetAction[];
}

export interface Step1CreativeBetVariant {
    bet: CreativeBet;
    betIndex: number;
    step1Subject?: string;
    step1Copy: string;
}

const STEP_ONE_LABEL_PATTERN = /^step\s*1(?:\b|\s|:|-)/i;

function step1PreviewForCreativeBet(bet: CreativeBet): CreativeBetSequencePreviewStep | null {
    if (bet.focus === 'followup-recovery') return null;
    return bet.sequencePreview?.find(step => STEP_ONE_LABEL_PATTERN.test(step.stepLabel.trim())) ?? null;
}

export function step1SubjectForCreativeBet(bet: CreativeBet): string | null {
    return step1PreviewForCreativeBet(bet)?.subject?.trim() || null;
}

export function step1CopyForCreativeBet(bet: CreativeBet): string | null {
    const explicitStepOne = step1PreviewForCreativeBet(bet);
    if (explicitStepOne?.copy.trim()) return explicitStepOne.copy;

    if (bet.sequencePreview?.length || bet.focus === 'followup-recovery') return null;
    return bet.preview.trim() || null;
}

export function isStep1AbEligible(bet: CreativeBet): boolean {
    return step1CopyForCreativeBet(bet) !== null;
}

export function toggleStep1AbSelection(
    bets: CreativeBet[],
    selectedIndexes: number[],
    betIndex: number,
    maxSelections = 3,
): number[] {
    if (betIndex < 0 || betIndex >= bets.length) return selectedIndexes;
    if (selectedIndexes.includes(betIndex)) return selectedIndexes.filter(index => index !== betIndex);
    if (!isStep1AbEligible(bets[betIndex]) || selectedIndexes.length >= maxSelections) return selectedIndexes;
    return [...selectedIndexes, betIndex];
}

export function selectedStep1AbVariants(bets: CreativeBet[], selectedIndexes: number[]): Step1CreativeBetVariant[] {
    const selectedIndexSet = new Set(selectedIndexes);
    return bets.flatMap((bet, betIndex) => {
        if (!selectedIndexSet.has(betIndex)) return [];
        const step1Copy = step1CopyForCreativeBet(bet);
        if (!step1Copy) return [];
        const step1Subject = step1SubjectForCreativeBet(bet);
        return [{ bet, betIndex, ...(step1Subject ? { step1Subject } : {}), step1Copy }];
    });
}

export function reconcileSelectedBetIndexes(bets: CreativeBet[], selectedIndexes: number[]): number[] {
    const selectedIndexSet = new Set(selectedIndexes);
    return bets.flatMap((bet, index) => selectedIndexSet.has(index) && isStep1AbEligible(bet) ? [index] : []);
}
